import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const RBACContext = createContext({});

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) throw new Error('useRBAC must be used within RBACProvider');
  return context;
};

// Permission definitions per role
const PERMISSIONS = {
  admin: {
    canViewDashboard: true,
    canManageUsers: true,
    canCreateData: true,
    canEditData: true,
    canDeleteData: true,
    canViewAllRequests: true,
    canApproveRequests: true,
    canManageRooms: true,
    canViewAnalytics: true,
    canViewReports: true,
    canManageCheckin: true,
    canViewResidents: true,
    canSubmitRequest: true,
    canManageResidentServices: true, // Admin can add/edit/delete in Resident Services
    canViewStayHistory: true,
    canViewCalendar: true,
    canViewRoomManagement: true,
    canViewCheckin: true,
    canViewOccupancyAnalytics: true,
    canViewAdvancedReports: true,
    canAccessAdministration: true,
  },
  viewer: {
    canViewDashboard: true,
    canManageUsers: false,
    canCreateData: false,
    canEditData: false,
    canDeleteData: false,
    canViewAllRequests: true,
    canApproveRequests: false,
    canManageRooms: false,
    canViewAnalytics: true,
    canViewReports: true,
    canManageCheckin: false,
    canViewResidents: true,
    canSubmitRequest: false,
    canManageResidentServices: false,
    canViewStayHistory: true,
    canViewCalendar: true,
    canViewRoomManagement: false,
    canViewCheckin: true,
    canViewOccupancyAnalytics: true,
    canViewAdvancedReports: true,
    canAccessAdministration: false,
  },
  guest: {
    canViewDashboard: false,
    canManageUsers: false,
    canCreateData: false,
    canEditData: false,
    canDeleteData: false,
    canViewAllRequests: false,
    canApproveRequests: false,
    canManageRooms: false,
    canViewAnalytics: false,
    canViewReports: false,
    canManageCheckin: false,
    canViewResidents: false,
    canSubmitRequest: true,
    canManageResidentServices: false,
    canViewStayHistory: false,      // Guests cannot access Stay History
    canViewCalendar: false,         // Guests cannot access Calendar View
    canViewRoomManagement: false,   // Guests cannot access Room Management
    canViewCheckin: false,          // Guests cannot access Check-in/out
    canViewOccupancyAnalytics: false, // Guests cannot access Occupancy Analytics
    canViewAdvancedReports: false,  // Guests cannot access Advanced Reports
    canAccessAdministration: false, // Guests cannot access Administration modules
  },
};

// Routes accessible per role
export const ROLE_ROUTES = {
  admin: [
    '/building-performance',
    '/occupancy-analytics',
    '/resident-checkin',
    '/room-management',
    '/calendar-view',
    '/reservation-request',
    '/advanced-reporting',
    '/email-template-editor',
    '/user-management',
    '/claims-dashboard',
    '/restaurant',
    '/meal-ticket-system',
    '/bulk-import',
    '/lost-found',
    '/campus-map',
    '/emergency-contacts',
    '/help-center',
    '/notification-center',
    '/stay-history',
    '/resident-services-admin',
  ],
  viewer: [
    '/building-performance',
    '/occupancy-analytics',
    '/resident-checkin',
    '/calendar-view',
    '/reservation-request',
    '/advanced-reporting',
    '/restaurant',
    '/meal-ticket-system',
    '/lost-found',
    '/campus-map',
    '/emergency-contacts',
    '/help-center',
    '/notification-center',
    '/stay-history',
  ],
  guest: [
    // Guests can ONLY access these routes
    '/reservation-request',
    '/lost-found',
    '/campus-map',
    '/emergency-contacts',
    '/help-center',
    '/notification-center',
    // Explicitly excluded: /stay-history, /calendar-view, /room-management,
    // /resident-checkin, /occupancy-analytics, /advanced-reporting,
    // /resident-services-admin, /user-management, /building-performance, etc.
  ],
};

export const DEFAULT_REDIRECT = {
  admin: '/occupancy-analytics',
  viewer: '/occupancy-analytics',
  guest: '/reservation-request',
};

export const RBACProvider = ({ children }) => {
  const { user, userProfile } = useAuth();

  const role = useMemo(() => {
    return user?.user_metadata?.role || userProfile?.role || 'guest';
  }, [user, userProfile]);

  const permissions = useMemo(() => {
    return PERMISSIONS?.[role] || PERMISSIONS?.guest;
  }, [role]);

  const can = (permission) => {
    return permissions?.[permission] === true;
  };

  const canAccessRoute = (path) => {
    const allowedRoutes = ROLE_ROUTES?.[role] || ROLE_ROUTES?.guest;
    return allowedRoutes?.includes(path);
  };

  const value = {
    role,
    permissions,
    can,
    canAccessRoute,
    isAdmin: role === 'admin',
    isViewer: role === 'viewer',
    isGuest: role === 'guest',
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export default RBACContext;
